import { useState, useEffect, useCallback, useRef } from "react";

interface VisualViewportState {
  keyboardHeight: number;
  viewportHeight: number;
  offsetTop: number;
  isKeyboardOpen: boolean;
}

const INITIAL: VisualViewportState = {
  keyboardHeight: 0,
  viewportHeight: typeof window !== "undefined" ? window.innerHeight : 0,
  offsetTop: 0,
  isKeyboardOpen: false,
};

export function useVisualViewport(): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(INITIAL);
  // rAF handle so bursts of visualViewport scroll/resize events (iOS fires
  // these continuously while the on-screen keyboard is up and the caret moves)
  // collapse into a single measurement per frame instead of one setState each.
  const rafRef = useRef<number | null>(null);

  const measure = useCallback(() => {
    rafRef.current = null;
    const vv = window.visualViewport;
    if (!vv) return;

    const layoutHeight = window.innerHeight;
    // Round every value: iOS reports sub-pixel viewport heights that wobble by
    // fractions on each keystroke. Without rounding the drawer's inline
    // bottom/maxHeight recompute every frame and the sheet visibly jitters
    // while the guest types.
    const viewportHeight = Math.round(vv.height);
    const offsetTop = Math.round(vv.offsetTop);
    const keyboardHeight = Math.max(0, Math.round(layoutHeight - vv.height - vv.offsetTop));
    const isKeyboardOpen = keyboardHeight > 50;

    setState((prev) => {
      // Bail out when nothing meaningful moved so we don't re-render (and
      // reflow the drawer) on redundant scroll events.
      if (
        prev.keyboardHeight === keyboardHeight &&
        prev.viewportHeight === viewportHeight &&
        prev.offsetTop === offsetTop &&
        prev.isKeyboardOpen === isKeyboardOpen
      ) {
        return prev;
      }
      return { keyboardHeight, viewportHeight, offsetTop, isKeyboardOpen };
    });
  }, []);

  const schedule = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(measure);
  }, [measure]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    measure();

    vv.addEventListener("resize", schedule);
    vv.addEventListener("scroll", schedule);

    return () => {
      vv.removeEventListener("resize", schedule);
      vv.removeEventListener("scroll", schedule);
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [measure, schedule]);

  return state;
}
